import { Typography } from '@capsule/consumer-design-system';

import { useTranslations } from 'next-intl';
import { css } from 'styled-components';

import PharmacyIcon from 'public/old_pharmacy.svg';
import PillBottleIcon from 'public/pill-bottle-2.svg';
import MarketingPageSection from 'src/app/components/Elements/MarketingPageSection';
import type { PharmacyHubEntryResponse } from 'src/app/hooks/data/pharmacyHub';
import { useIsSpanishRoute } from 'src/app/hooks/useIsSpanishRoute';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';

import { ClearRxLayout } from '../ClearRxLayout';
import { CLEARRX_PHONE, LANGUAGES_SUPPORTED } from '../constants';
import { Capitalize, ConfirmContainer, ConfirmIconContainer, ConfirmTextContainer, StyledInlineSvg } from '../styles';

import type { SelectedPharmacy } from '.';

interface ConfirmationSuccessProps {
  selectedPharmacy: SelectedPharmacy;
  pendingEscripts: PharmacyHubEntryResponse;
}

export const ConfirmationSuccess = (props: ConfirmationSuccessProps) => {
  const { selectedPharmacy, pendingEscripts } = props;
  const t = useTranslations('pharmacySelection.success');
  const isSpanishRoute = useIsSpanishRoute();

  useSegmentEvent('viewClearRxPharmacySelectionConfirmation', {
    shouldTrackOnMount: true,
    payload: {
      pharmacy_name: selectedPharmacy.name,
      pharmacy_address: selectedPharmacy.address || '',
      provider_npi: pendingEscripts.provider.npi,
      provider_name: `${pendingEscripts.provider.first_name} ${pendingEscripts.provider.last_name}`,
      practice_name: pendingEscripts.provider.practice_name ?? '',
      emr_department_name: pendingEscripts.department?.name || '',
      emr_department_id: pendingEscripts.department?.emr_department_id || '',
      language_used: isSpanishRoute ? LANGUAGES_SUPPORTED.SPANISH : LANGUAGES_SUPPORTED.ENGLISH,
    },
  });

  const isComplete = selectedPharmacy.name === 'Capsule';
  const status = isComplete ? t('currentStatus.completed') : t('currentStatus.inProgress');

  return (
    <MarketingPageSection>
      <ClearRxLayout>
        <Typography
          variant="heading1"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s7};
          `}
        >
          {t('title', { status: status, pharmacy: selectedPharmacy.name })}
        </Typography>
        <ConfirmContainer>
          <ConfirmIconContainer>
            <StyledInlineSvg src={PharmacyIcon} size="22" />
          </ConfirmIconContainer>
          <ConfirmTextContainer>
            <Typography variant="body">
              {selectedPharmacy.name === 'Capsule' ? t('capsulePharmacyName') : selectedPharmacy.name}
            </Typography>
            {selectedPharmacy.name !== 'Capsule' && <Typography variant="body">{selectedPharmacy.address}</Typography>}
          </ConfirmTextContainer>
        </ConfirmContainer>
        <ConfirmContainer>
          <ConfirmIconContainer>
            <StyledInlineSvg src={PillBottleIcon} size="24" />
          </ConfirmIconContainer>
          <ConfirmTextContainer>
            <Typography variant="body">
              <Capitalize>
                {pendingEscripts.medications.map(med => `${med.primary_name} ${med.strength ?? ''}`).join(', ')}
              </Capitalize>
            </Typography>
          </ConfirmTextContainer>
        </ConfirmContainer>
        <Typography
          variant="overline"
          css={css`
            color: ${({ theme }) => theme.colors.capsuleGreen80};
            margin-bottom: ${({ theme }) => theme.space.s2};
            margin-top: ${({ theme }) => theme.space.s6};
          `}
        >
          {t('whatsNext')}
        </Typography>
        <Typography
          variant="body"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s5};
          `}
        >
          {selectedPharmacy.name == 'Capsule' ? t('capsuleConfirmationMessage') : t('pharmacyConfirmationMessage')}
        </Typography>
        <Typography
          variant="overline"
          css={css`
            color: ${({ theme }) => theme.colors.capsuleGreen80};
            margin-bottom: ${({ theme }) => theme.space.s2};
            margin-top: ${({ theme }) => theme.space.s6};
          `}
        >
          {t('questionsLabel')}
        </Typography>
        <Typography
          variant="body"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s5};
          `}
        >
          {t.rich('contactMessage', {
            phone: () => <a href={`tel:${CLEARRX_PHONE}`}>{CLEARRX_PHONE}</a>,
            serviceType: () => (selectedPharmacy.name === 'Capsule' ? t('delivery') : t('pharmacyPickup')),
          })}
        </Typography>
      </ClearRxLayout>
    </MarketingPageSection>
  );
};
